import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, Copy, ShieldCheck, ShieldAlert, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { requireAdmin } from "@/lib/admin-guard";
import {
  getPaypalAdminOverview,
  saveWebhookId,
  setPaypalPublicEnabled,
  adminLookupPaypalSubscription,
} from "@/lib/paypal-subscriptions.functions";

// Admin console for the live PayPal integration:
//  - Register the webhook_id you get from the PayPal Developer Dashboard.
//  - Toggle the public feature flag once the controlled live test passes.
//  - Inspect the 50 most recent subscriptions and pull the live PayPal
//    view for any individual subscription id.
//
// Live webhook URL to register at PayPal Developer Dashboard → Apps &
// Credentials → Live app → Add Webhook:
//     https://www.recoverytrauma.com/api/public/webhooks/paypal
// Subscribe to: BILLING.SUBSCRIPTION.* and PAYMENT.SALE.*

const WEBHOOK_URL = "https://www.recoverytrauma.com/api/public/webhooks/paypal";

export const Route = createFileRoute("/admin_/paypal/ops")({
  ssr: false,
  beforeLoad: requireAdmin,
  head: () => ({
    meta: [
      { title: "PayPal Ops | Admin" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: AdminPaypalOpsPage,
});

type Overview = Awaited<ReturnType<typeof getPaypalAdminOverview>>;

function AdminPaypalOpsPage() {
  const fetchOverview = useServerFn(getPaypalAdminOverview);
  const saveWebhook = useServerFn(saveWebhookId);
  const setPublic = useServerFn(setPaypalPublicEnabled);
  const lookup = useServerFn(adminLookupPaypalSubscription);

  const [overview, setOverview] = useState<Overview | null>(null);
  const [loading, setLoading] = useState(true);
  const [webhookId, setWebhookId] = useState("");
  const [savingWebhook, setSavingWebhook] = useState(false);
  const [togglePending, setTogglePending] = useState(false);
  const [lookupId, setLookupId] = useState("");
  const [lookupResult, setLookupResult] = useState<
    Awaited<ReturnType<typeof adminLookupPaypalSubscription>> | null
  >(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  async function refresh() {
    setLoading(true);
    try {
      const r = await fetchOverview();
      setOverview(r);
      const cfg = r.config.find((c) => c.key === "webhook") as
        | { value: { id?: string } | null }
        | undefined;
      if (cfg?.value?.id && !webhookId) setWebhookId(cfg.value.id);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Failed to load overview");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const configMap = new Map(
    (overview?.config ?? []).map((c) => [c.key, c.value as { id?: string } | null]),
  );
  const productId = configMap.get("product")?.id ?? null;
  const planId = configMap.get("plan")?.id ?? null;
  const savedWebhookId = configMap.get("webhook")?.id ?? null;

  return (
    <div className="mx-auto max-w-4xl space-y-8 p-6">
      <div>
        <Link to="/admin/paypal" className="text-xs text-[var(--gold-deep)] underline">
          ← PayPal setup
        </Link>
        <h1 className="mt-2 font-display text-3xl">PayPal Ops</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Live production controls. The public PayPal button on <code>/membership</code> stays
          hidden until the controlled test passes and you flip the switch below.
        </p>
      </div>

      <section className="space-y-3 rounded-2xl border border-border bg-card p-5">
        <h2 className="font-display text-lg">Webhook</h2>
        <div className="rounded-lg border border-border bg-muted/30 p-3">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Live webhook URL (paste into PayPal Developer Dashboard)
          </div>
          <div className="mt-1 flex items-center justify-between gap-2">
            <code className="break-all text-sm">{WEBHOOK_URL}</code>
            <button
              onClick={() => {
                navigator.clipboard.writeText(WEBHOOK_URL);
                toast.success("Copied");
              }}
              className="rounded-md border border-border p-1.5 hover:bg-muted"
              aria-label="Copy webhook URL"
            >
              <Copy className="h-3.5 w-3.5" />
            </button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Subscribe the webhook to <code>BILLING.SUBSCRIPTION.*</code> and{" "}
            <code>PAYMENT.SALE.*</code>. Then paste the Webhook ID PayPal shows you
            below.
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            type="text"
            value={webhookId}
            onChange={(e) => setWebhookId(e.target.value.trim())}
            placeholder="Webhook ID (e.g. 8PT597110X687430LGLNZQKA)"
            className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm"
          />
          <button
            onClick={async () => {
              setSavingWebhook(true);
              try {
                await saveWebhook({ data: { webhook_id: webhookId } } as never);
                toast.success("Webhook ID saved");
                refresh();
              } catch (e) {
                toast.error(e instanceof Error ? e.message : "Failed");
              } finally {
                setSavingWebhook(false);
              }
            }}
            disabled={savingWebhook || webhookId.length < 6}
            className="btn-gold px-5"
          >
            {savingWebhook ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}
          </button>
        </div>
        {savedWebhookId ? (
          <p className="text-xs text-muted-foreground">
            Currently saved: <code>{savedWebhookId}</code>
          </p>
        ) : (
          <p className="text-xs text-amber-600">
            No webhook ID saved yet — webhook deliveries will be rejected until one is set.
          </p>
        )}
      </section>

      <section className="space-y-3 rounded-2xl border border-border bg-card p-5">
        <h2 className="font-display text-lg">Public checkout</h2>
        <div className="flex items-center justify-between gap-4 rounded-lg border border-border bg-muted/30 p-3">
          <div className="flex items-center gap-3">
            {overview?.public_enabled ? (
              <ShieldCheck className="h-5 w-5 text-[var(--gold-deep)]" />
            ) : (
              <ShieldAlert className="h-5 w-5 text-amber-600" />
            )}
            <div className="text-sm">
              PayPal button on <code>/membership</code> is{" "}
              <strong>{overview?.public_enabled ? "visible to everyone" : "admin-only"}</strong>.
            </div>
          </div>
          <button
            onClick={async () => {
              const target = !overview?.public_enabled;
              if (target && !confirm("Enable PayPal for ALL users?")) return;
              setTogglePending(true);
              try {
                await setPublic({ data: { enabled: target } } as never);
                toast.success(target ? "PayPal is now public" : "PayPal is admin-only again");
                refresh();
              } catch (e) {
                toast.error(e instanceof Error ? e.message : "Failed");
              } finally {
                setTogglePending(false);
              }
            }}
            disabled={togglePending}
            className="btn-gold px-5"
          >
            {togglePending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : overview?.public_enabled ? (
              "Disable public"
            ) : (
              "Enable public"
            )}
          </button>
        </div>
        <p className="text-xs text-muted-foreground">
          Prerequisites before enabling: webhook ID saved, controlled live subscription completed,
          $14.99 first payment received, welcome email sent once, cancellation & refund flows checked.

        </p>
      </section>

      <section className="space-y-3 rounded-2xl border border-border bg-card p-5">
        <h2 className="font-display text-lg">Config</h2>
        <div className="grid gap-2 sm:grid-cols-2">
          <div className="rounded-lg border border-border bg-muted/30 p-3">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Product ID
            </div>
            <code className="text-sm">{productId ?? "—"}</code>
          </div>
          <div className="rounded-lg border border-border bg-muted/30 p-3">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">
              Plan ID
            </div>
            <code className="text-sm">{planId ?? "—"}</code>
          </div>
        </div>
      </section>

      <section className="space-y-3 rounded-2xl border border-border bg-card p-5">
        <h2 className="font-display text-lg">Recent subscriptions</h2>
        {loading ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : (overview?.subscriptions.length ?? 0) === 0 ? (
          <p className="text-sm text-muted-foreground">No PayPal subscriptions yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-start text-xs">
              <thead className="text-[10px] uppercase text-muted-foreground">
                <tr>
                  <th className="px-2 py-2">Subscription</th>
                  <th className="px-2 py-2">User</th>
                  <th className="px-2 py-2">Email</th>
                  <th className="px-2 py-2">Status</th>
                  <th className="px-2 py-2">Setup fee</th>
                  <th className="px-2 py-2">Next billing</th>
                  <th className="px-2 py-2">Paid through</th>
                  <th className="px-2 py-2">Created</th>
                </tr>
              </thead>
              <tbody>
                {overview!.subscriptions.map((s) => (
                  <tr key={s.paypal_subscription_id} className="border-t border-border">
                    <td className="px-2 py-2">
                      <code>{s.paypal_subscription_id}</code>
                    </td>
                    <td className="px-2 py-2">
                      <code className="text-[10px]">{s.user_id.slice(0, 8)}…</code>
                    </td>
                    <td className="px-2 py-2">{s.paypal_payer_email ?? "—"}</td>
                    <td className="px-2 py-2">{s.status}</td>
                    <td className="px-2 py-2">{s.setup_fee_status}</td>
                    <td className="px-2 py-2">
                      {s.next_billing_time
                        ? new Date(s.next_billing_time).toISOString().slice(0, 10)
                        : "—"}
                    </td>
                    <td className="px-2 py-2">
                      {s.paid_through
                        ? new Date(s.paid_through).toISOString().slice(0, 10)
                        : "—"}
                    </td>
                    <td className="px-2 py-2">
                      {new Date(s.created_at).toISOString().slice(0, 10)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="space-y-3 rounded-2xl border border-border bg-card p-5">
        <h2 className="font-display text-lg">Look up subscription (live PayPal)</h2>
        <div className="flex flex-col gap-2 sm:flex-row">
          <input
            type="text"
            value={lookupId}
            onChange={(e) => setLookupId(e.target.value.trim())}
            placeholder="I-XXXXXXX (PayPal subscription id)"
            className="flex-1 rounded-lg border border-border bg-background px-3 py-2 text-sm"
          />
          <button
            onClick={async () => {
              setLookupLoading(true);
              setLookupResult(null);
              try {
                const r = await lookup({ data: { subscription_id: lookupId } } as never);
                setLookupResult(r);
              } catch (e) {
                toast.error(e instanceof Error ? e.message : "Failed");
              } finally {
                setLookupLoading(false);
              }
            }}
            disabled={lookupLoading || lookupId.length < 6}
            className="btn-gold px-5"
          >
            {lookupLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Look up"}
          </button>
        </div>
        {lookupResult ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <div className="text-[10px] uppercase text-muted-foreground">Local row</div>
              <pre className="max-h-96 overflow-auto rounded-lg bg-muted/40 p-2 text-[10px]">
                {JSON.stringify(lookupResult.local, null, 2)}
              </pre>
            </div>
            <div>
              <div className="text-[10px] uppercase text-muted-foreground">Live PayPal</div>
              <pre className="max-h-96 overflow-auto rounded-lg bg-muted/40 p-2 text-[10px]">
                {JSON.stringify(lookupResult.live, null, 2)}
              </pre>
            </div>
          </div>
        ) : null}
        <p className="text-xs text-muted-foreground">
          Open in PayPal Dashboard →{" "}
          <a
            className="inline-flex items-center gap-1 text-[var(--gold-deep)] underline"
            href="https://www.paypal.com/billing/subscriptions"
            target="_blank"
            rel="noreferrer"
          >
            Subscriptions
            <ExternalLink className="h-3 w-3" />
          </a>
        </p>
      </section>
    </div>
  );
}
